// src/hooks/useWalletMobile.js - VERSÃO CORRIGIDA E SIMPLIFICADA
import { useState, useEffect, useCallback } from 'react';

// ✅ DETECÇÃO SIMPLIFICADA E FUNCIONAL
const detectMobileWallet = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const hasInjectedWeb3 = !!(window.ethereum || (window.web3 && window.web3.currentProvider));
  
  return {
    isMobileBrowser: isMobile,
    hasInjectedWeb3,
    isMetaMaskMobile: !!(window.ethereum && window.ethereum.isMetaMask),
    isWalletConnectCapable: false, // Simplificado por enquanto
    provider: window.ethereum || (window.web3 && window.web3.currentProvider)
  };
};

// ✅ HOOK CORRIGIDO E FUNCIONAL
export const useWalletMobile = () => {
  const [walletState, setWalletState] = useState({
    isConnected: false,
    isConnecting: false,
    currentAccount: null,
    balance: null,
    isPolygon: false,
    error: null,
    walletType: null
  });

  const [walletInfo, setWalletInfo] = useState(detectMobileWallet());

  // ✅ CONEXÃO SIMPLIFICADA E FUNCIONAL
  const connectWallet = useCallback(async () => {
    setWalletState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      const provider = walletInfo.provider;
      
      if (!provider) {
        // ✅ REDIRECIONAMENTO MOBILE PARA META MASK
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          const deepLink = `https://metamask.app.link/dapp/${window.location.hostname}${window.location.pathname}`;
          window.location.href = deepLink;
          return { redirect: true };
        }
        
        throw new Error('Instale MetaMask ou use em mobile com carteira');
      }

      // ✅ SOLICITA ACESSO ÀS CONTAS
      const accounts = await provider.request({
        method: 'eth_requestAccounts'
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('Nenhuma conta conectada');
      }

      // ✅ VERIFICA REDE
      const chainId = await provider.request({ method: 'eth_chainId' });
      const isPolygon = chainId === '0x89'; // Polygon Mainnet

      // ✅ ATUALIZA ESTADO
      setWalletState({
        isConnected: true,
        isConnecting: false,
        currentAccount: accounts[0],
        balance: '0', // Será atualizado depois
        isPolygon,
        error: null,
        walletType: 'injected'
      });

      console.log('✅ Carteira mobile conectada:', accounts[0]);
      return { success: true, account: accounts[0] };

    } catch (error) {
      console.error('❌ Erro ao conectar carteira mobile:', error);
      
      let errorMessage = error.message;
      if (error.code === 4001) {
        errorMessage = 'Conexão rejeitada pelo usuário';
      }

      setWalletState(prev => ({
        ...prev,
        isConnecting: false,
        error: errorMessage
      }));

      return { success: false, error: errorMessage };
    }
  }, [walletInfo.provider]);

  // ✅ VERIFICA CONEXÃO EXISTENTE AO CARREGAR
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (walletInfo.provider) {
        try {
          const accounts = await walletInfo.provider.request({
            method: 'eth_accounts'
          });

          if (accounts.length > 0) {
            const chainId = await walletInfo.provider.request({ method: 'eth_chainId' });
            
            setWalletState(prev => ({
              ...prev,
              isConnected: true,
              currentAccount: accounts[0],
              isPolygon: chainId === '0x89'
            }));
          }
        } catch (error) {
          console.log('ℹ️ Nenhuma conexão existente');
        }
      }
    };

    checkExistingConnection();
  }, [walletInfo.provider]);

  // ✅ LISTENERS PARA MUDANÇAS
  useEffect(() => {
    const provider = walletInfo.provider;
    if (!provider) return;

    const handleAccountsChanged = (accounts) => {
      console.log('📱 Contas alteradas:', accounts);
      if (accounts.length === 0) {
        setWalletState(prev => ({ ...prev, isConnected: false, currentAccount: null }));
      } else {
        setWalletState(prev => ({ ...prev, currentAccount: accounts[0] }));
      }
    };

    const handleChainChanged = (chainId) => {
      console.log('📱 Rede alterada:', chainId);
      setWalletState(prev => ({ ...prev, isPolygon: chainId === '0x89' }));
    };

    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('chainChanged', handleChainChanged);

    return () => {
      provider.removeListener('accountsChanged', handleAccountsChanged);
      provider.removeListener('chainChanged', handleChainChanged);
    };
  }, [walletInfo.provider]);

  // ✅ DESCONECTAR
  const disconnectWallet = useCallback(() => {
    setWalletState({
      isConnected: false,
      isConnecting: false,
      currentAccount: null,
      balance: null,
      isPolygon: false,
      error: null,
      walletType: null
    });
  }, []);

  // ✅ TROCA PARA POLYGON
  const switchToPolygon = useCallback(async () => {
    try {
      if (!walletInfo.provider) {
        throw new Error('Carteira não conectada');
      }

      await walletInfo.provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }],
      });

      setWalletState(prev => ({ ...prev, isPolygon: true }));
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao trocar para Polygon:', error);
      setWalletState(prev => ({ ...prev, error: error.message }));
      return { success: false, error: error.message };
    }
  }, [walletInfo.provider]);

  return {
    // Estado
    ...walletState,
    walletInfo,
    
    // Ações
    connectWallet,
    disconnectWallet,
    switchToPolygon,
    
    // Utilitários
    shouldShowMobileOptimizedUI: walletInfo.isMobileBrowser && !walletInfo.hasInjectedWeb3,
    canUseWalletConnect: false, // Simplificado
    needsPolygonSwitch: walletState.isConnected && !walletState.isPolygon
  };
};

export default useWalletMobile;
